import React, { useMemo } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Box, Stack, Typography, Icons } from "@curves/ui-base";
import { TimeLine, Table, ProductInfo } from '@curves/ui-shared';
import { parseDate } from "@util/moment";
import { isEqual } from "@util/utils";
import { CLUB_STOCK_DOCUMENT_TYPE } from "@clubInventory/constants/dataEnums";

// 功能：入庫紀錄狀態碼（API recordStatusColor）
export const RECORD_STATUS_CODE = {
	INFO: 3, // 灰 正常作業
	SUCCESS: 1, // 綠 成功
	ERROR: 9, // 紅 警告
};

// 功能：入庫紀錄顯示狀態（對應時間軸/內容樣式）
export const DISPLAY_STATUS = {
	ERROR: "error",
	SUCCESS: "success",
	DEFAULT: "default",
};

// 功能：recordStatusColor → displayStatus
export const getDisplayStatus = recordStatusColor => {
	if (recordStatusColor === RECORD_STATUS_CODE.ERROR) return DISPLAY_STATUS.ERROR;
	if (recordStatusColor === RECORD_STATUS_CODE.SUCCESS) return DISPLAY_STATUS.SUCCESS;
	return DISPLAY_STATUS.DEFAULT;
};

// 功能：正規化後端入庫紀錄欄位（orderDate / recordSatus 相容）
export const normalizeStockInRecord = record => ({
	...record,
	occurredAt: record.orderDate ?? record.occurredAt,
	recordStatusColor: record.recordSatus ?? record.recordStatusColor,
});

// 功能：文字紀錄內容（狀態圖示 + 訊息）
const TextRecordContent = ({ record }) => {
	const status = getDisplayStatus(record.recordStatusColor);
	const { message } = record;
	const isError = status === DISPLAY_STATUS.ERROR;
	const Icon = isError ? Icons.Close : Icons.Check;
	const color = isError ? "error" : "success";

	return (
		<Stack direction="row" alignItems="center" spacing={1} className={clsx("stock-in-record-text", `stock-in-record-text-${status}`)}>
			<Box className={clsx("stock-in-record-icon-wrapper", `stock-in-record-icon-wrapper-${color}`)}>
				<Icon className="stock-in-record-icon" style={{ fontSize: "1.25rem" }} htmlColor="#fff" />
			</Box>

			<Typography color={color}>{message}</Typography>
		</Stack>
	);
};

TextRecordContent.propTypes = {
	record: PropTypes.shape({
		recordStatusColor: PropTypes.number,
		message: PropTypes.string,
	}).isRequired,
};

// 功能：商品清單紀錄表頭（依 clubStockDocumentType 顯示入庫/未入庫；quantity 欄 formatCell 閉包 quantityColor）
const getProductListHeaderRow = (isUnstocked, quantityColor = "primary") => [
	{
		cellKey: "productName",
		headerLabel: "商品清單",
		minWidth: 200,
		formatCell: (productName, row) => (
			<ProductInfo
				product={{
					sku: row.sku,
					name: productName,
					spec: row.variantName,
				}}
				showImage={false}
			/>
		),
	},
	{
		cellKey: "quantity",
		headerLabel: isUnstocked ? "未入庫" : "入庫",
		align: "center",
		minWidth: 100,
		className: `text-${quantityColor}`,
		formatCell: quantity => <Typography className={`text-${quantityColor}`}>{quantity || 0}</Typography>,
	},
];

// 功能：商品清單紀錄內容（items）
const ProductListRecordContent = React.memo(({ record }) => {
	const list = record.items || [];
	const status = getDisplayStatus(record.recordStatusColor);
	const isUnstocked = isEqual(record.clubStockDocumentType, CLUB_STOCK_DOCUMENT_TYPE.EXCEPTION);
	const quantityColor = status === DISPLAY_STATUS.ERROR ? "error" : status === DISPLAY_STATUS.SUCCESS ? "success" : "primary";
	const headerRow = useMemo(() => getProductListHeaderRow(isUnstocked, quantityColor), [isUnstocked, quantityColor]);

	return (
		<Box className={clsx("stock-in-record-product-list", `stock-in-record-product-list-${status}`)}>
			<Table rowKey={(row, index) => `${row.productID}-${row.variantID}-${index}`} cardProps={{ className: "theme-gray", elevation: 0 }}>
				<Table.Header headerRow={headerRow} />
				<Table.Body headerRow={headerRow} rowsData={list} />
			</Table>
		</Box>
	);
});

ProductListRecordContent.displayName = "ProductListRecordContent";
ProductListRecordContent.propTypes = {
	record: PropTypes.shape({
		recordStatusColor: PropTypes.number,
		clubStockDocumentType: PropTypes.number,
		items: PropTypes.array,
	}).isRequired,
};

// 功能：單筆時間軸內容（message / items 擇一）
const RecordContent = ({ record }) => {
	let content = null;
	if (record.message) {
		content = <TextRecordContent record={record} />;
	} else if (Array.isArray(record.items) && record.items.length > 0) {
		content = <ProductListRecordContent record={record} />;
	}

	const status = getDisplayStatus(record.recordStatusColor);
	const contentClassName = clsx(
		"stock-in-record-content",
		status === DISPLAY_STATUS.SUCCESS && "stock-in-record-content-success",
		status === DISPLAY_STATUS.ERROR && "stock-in-record-content-error",
	);

	return <Box className={contentClassName}>{content}</Box>;
};

RecordContent.propTypes = {
	record: PropTypes.object.isRequired,
};

// 功能：進貨單入庫紀錄（時間軸）
const PurchaseOrderStockInRecords = ({ records = [], className }) => {
	const rawList = records || [];
	const list = rawList.map(normalizeStockInRecord);
	const hasRecords = list.length > 0;

	if (!hasRecords) return null;

	return (
		<Box className={clsx("purchase-order-stock-in-records", className)}>
			<TimeLine>
				{list.map((record, index) => (
					<TimeLine.Item key={index}>
						<TimeLine.ItemTitle className="stock-in-record-title">
							<Stack spacing={2} justifyContent="space-between" alignItems="center">
								<Typography variant="body2" className="mb-0">
									日期: {parseDate(record.occurredAt)}
								</Typography>
								<Typography variant="body2" className="mb-0">
									擔當: {record.employeeName}
								</Typography>
							</Stack>
						</TimeLine.ItemTitle>
						<TimeLine.ItemContent>
							<RecordContent record={record} />
						</TimeLine.ItemContent>
					</TimeLine.Item>
				))}
			</TimeLine>
		</Box>
	);
};

const recordItemShape = PropTypes.shape({
	productID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	variantID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	sku: PropTypes.string,
	productName: PropTypes.string,
	variantName: PropTypes.string,
	pictureSrc: PropTypes.string,
	quantity: PropTypes.number,
});

PurchaseOrderStockInRecords.propTypes = {
	records: PropTypes.arrayOf(
		PropTypes.shape({
			recordId: PropTypes.string,
			recordSatus: PropTypes.oneOf([1, 2, 3]),
			clubStockDocumentType: PropTypes.number,
			orderDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
			employeeId: PropTypes.number,
			employeeName: PropTypes.string,
			message: PropTypes.string,
			items: PropTypes.arrayOf(recordItemShape),
		}),
	),
	className: PropTypes.string,
	height: PropTypes.string,
	emptyMessage: PropTypes.string,
	useMockData: PropTypes.bool,
};

export default PurchaseOrderStockInRecords;
